import { motion } from "framer-motion";
import { Heart, MessageCircle, Send, Bookmark } from "lucide-react";
import { useRef, useState } from "react";

const REELS = [
  {
    video: "https://firebasestorage.googleapis.com/v0/b/venueflow-calendar.firebasestorage.app/o/AQM-XWCOlCbMeeFVz-PQH9xH6i24N55YD1WOiy1gaBfCQZMt9osIXnqf03m2yiolp5LuzcLS9wwOoes6MsL1wNmQ_zXu8XWuNzrLiTU.mp4?alt=media&token=6f61f3d6-0c84-4305-8d90-ed1bdfce1414",
    handle: "@discoverwithde",
    name: "Discover With De",
    likes: "2,847",
    instagram: "https://www.instagram.com/reel/DMDbiORgTXg/",
    avatar: "https://firebasestorage.googleapis.com/v0/b/venueflow-calendar.firebasestorage.app/o/discover.jpg?alt=media&token=24f4076a-f94e-4d16-b8b2-c96204108c1e",
  },
  {
    video: "https://firebasestorage.googleapis.com/v0/b/venueflow-calendar.firebasestorage.app/o/AQMcCPAkc3AX1TcC8u45mSLDGsOdOsapQj4r6tg6NtN_faWEhX_CpNk0rg8Dv416MG_I8ljt7e46ryw6uBc1gZzIUyH0NTyyhwvBsUI.mp4?alt=media&token=9bd7507a-2c33-4436-af8d-1112330cbd5f",
    handle: "@mer.in.michigan",
    name: "Mer in Michigan",
    likes: "1,923",
    instagram: "https://www.instagram.com/reel/DMDlKcyMuwO/",
    avatar: "https://firebasestorage.googleapis.com/v0/b/venueflow-calendar.firebasestorage.app/o/mer.jpg?alt=media&token=76853bc8-86a5-4e1f-a3f8-5663e0fed731",
  },
  {
    video: "https://firebasestorage.googleapis.com/v0/b/venueflow-calendar.firebasestorage.app/o/AQNxd8jsfAZfMORbXcUopvuiqh7f69PJFU5-ygyUp2VXQ9BMNkoM9goEWvNWc5zcTOqgSLp1jg-nggOHFbqPx8Hs50f8EBmKbSaod49qp9u1MA.mp4?alt=media&token=d34e78a4-a2e8-4256-a519-9d387f2c29d4",
    handle: "@lex.go.here",
    name: "Lex Go Here",
    likes: "3,412",
    instagram: "https://www.instagram.com/reel/DMD1CuKPMbk/",
    avatar: "https://firebasestorage.googleapis.com/v0/b/venueflow-calendar.firebasestorage.app/o/lexgohere.jpg?alt=media&token=81829c23-a206-4b80-a339-83b5555e24dd",
  },
];

const ReelCard = ({ reel, index }: { reel: typeof REELS[0]; index: number }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [liked, setLiked] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      className="w-full max-w-[340px] group"
    >
      {/* Phone-style frame */}
      <div className="relative aspect-[9/16] w-full rounded-[28px] overflow-hidden shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] bg-yacht-charcoal ring-1 ring-white/10">
        {/* Video */}
        <video
          ref={videoRef}
          src={reel.video}
          className="absolute inset-0 w-full h-full object-cover"
          loop
          muted
          playsInline
          autoPlay
          preload="metadata"
        />

        {/* Gradient overlay at bottom */}
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />

        {/* Right side action buttons */}
        <div className="absolute right-3 bottom-24 flex flex-col items-center gap-5">
          <button
            onClick={() => setLiked(!liked)}
            className="flex flex-col items-center gap-1 transition-transform active:scale-90"
          >
            <Heart
              className={`w-7 h-7 drop-shadow-lg transition-colors ${
                liked ? "fill-red-500 text-red-500" : "text-white fill-white/20"
              }`}
            />
            <span className="text-white text-[11px] font-semibold drop-shadow">{reel.likes}</span>
          </button>
          <button className="flex flex-col items-center gap-1">
            <MessageCircle className="w-6 h-6 text-white drop-shadow-lg" />
          </button>
          <button className="flex flex-col items-center gap-1">
            <Send className="w-6 h-6 text-white drop-shadow-lg" />
          </button>
          <button className="flex flex-col items-center gap-1">
            <Bookmark className="w-6 h-6 text-white drop-shadow-lg" />
          </button>
        </div>

        {/* Bottom info */}
        <div className="absolute bottom-4 left-4 right-14">
          <a
            href={reel.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 mb-2"
          >
            <img
              src={reel.avatar}
              alt={reel.name}
              className="w-8 h-8 rounded-full object-cover shrink-0 ring-2 ring-white/30"
            />
            <span className="text-white text-sm font-bold drop-shadow-lg">{reel.handle}</span>
          </a>
        </div>
      </div>

      {/* Name below card */}
      <a
        href={reel.instagram}
        target="_blank"
        rel="noopener noreferrer"
        className="block mt-4 text-center"
      >
        <p className="text-sm font-semibold text-yacht-navy">{reel.name}</p>
        <p className="text-xs text-yacht-navy/40">{reel.handle}</p>
      </a>
    </motion.div>
  );
};

const LastYearSection = () => {
  return (
    <section className="bg-yacht-cream py-20 md:py-28 overflow-hidden">
      <div className="mx-auto max-w-7xl px-4">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-4 text-center font-serif text-4xl md:text-6xl tracking-tight text-yacht-navy"
        >
          Like Last Year…
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mb-14 text-center text-base md:text-lg text-yacht-navy/50 max-w-md mx-auto"
        >
          Don't just take our word for it.
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10 justify-items-center items-start">
          {REELS.map((reel, i) => (
            <ReelCard key={reel.handle} reel={reel} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default LastYearSection;
